import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, defaultIfEmpty, last, map, mergeMap, scan } from 'rxjs/operators';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { PlayerView } from '../types/dashboard.types';
import { resetOnSessionEnd } from '../utils/session-end.util';

/** Словарь «player_id → ФИО». Отсутствие ключа = имя неизвестно (это не пустое имя). */
export type PlayerNameMap = Record<string, string>;

/**
 * BE-1154: единственный резолвер имён игроков для админки.
 *
 * У победителей розыгрыша, узлов реферальной структуры и прочих списков в
 * контракте есть только `player_id` — имя добирается точечной ручкой
 * `GET /players/{player_id}`. Раньше эту задачу решал каждый экран по-своему
 * (четыре независимые реализации), и кэш с дедупликацией был лишь у одной.
 *
 * Здесь всё это в одном месте: дедупликация по id, кэш на время сессии вкладки,
 * ограничение числа одновременных запросов и деградация до усечённого id.
 *
 * **Когда кэш перестаёт быть верным.** Имя игрока меняет он сам в мини-аппе,
 * плюс админ может игрока удалить — то есть запись способна устареть. Живёт кэш
 * в пределах вкладки (перезагрузка страницы очищает его целиком), а точечно
 * сбрасывается через `invalidate(playerId)` — это делают экраны, которые сами
 * изменили игрока. Общий `clear()` — на случай смены пользователя: с BE-1480 сервис зовёт его сам при
 * конце сессии вкладки.
 *
 * **Батч-ручки на бэке нет**: `GET /admin/players` принимает только `offset` и
 * `limit`, выбрать игроков по списку id нельзя. Поэтому запросы точечные, но
 * пачками с ограниченной конкурентностью. Когда батч появится, менять придётся
 * только `fetchMissing` — потребители останутся как есть.
 */
@Injectable({ providedIn: 'root' })
export class PlayerNamesService {
  private readonly api = inject(ApiService);

  constructor() {
    resetOnSessionEnd(inject(AuthService), () => this.clear());
  }

  /** Сколько запросов имён держим в полёте одновременно. */
  static readonly CONCURRENCY = 5;

  /** id → ФИО. Только известные имена; пустое имя сюда не попадает. */
  private readonly names = new Map<string, string>();
  /**
   * id, по которым запрос уже уходил, — включая неудачные и вернувшие пустое имя.
   * Без этого таблица дёргала бы ручку по 404 на каждый повторный рендер.
   */
  private readonly requested = new Set<string>();

  /**
   * Имена для набора id: то, что уже в кэше, плюс догруженное. Отсутствующий
   * ключ означает «имя неизвестно» — экран сам решает, что показать.
   */
  resolve(ids: Iterable<string>): Observable<PlayerNameMap> {
    const wanted = [...new Set([...ids].filter((id) => !!id))];
    const missing = wanted.filter((id) => !this.requested.has(id));
    if (missing.length === 0) return of(this.snapshot(wanted));

    missing.forEach((id) => this.requested.add(id));
    return this.fetchMissing(missing).pipe(map(() => this.snapshot(wanted)));
  }

  /** Имя одного игрока; пустая строка — если неизвестно. */
  name(playerId?: string | null): Observable<string> {
    if (!playerId) return of('');
    return this.resolve([playerId]).pipe(map((names) => names[playerId] ?? ''));
  }

  /**
   * Имя игрока, а если оно неизвестно — усечённый id. Для таблиц, где ячейка не
   * может остаться пустой.
   */
  nameOrShortId(playerId: string, names?: PlayerNameMap): string {
    const name = (names ?? this.snapshot([playerId]))[playerId];
    return name || shortPlayerId(playerId);
  }

  /**
   * Положить в кэш имя из уже полученного объекта игрока. Так экраны, которые и
   * так грузят карточку, наполняют общий справочник без лишнего запроса.
   */
  prime(player: Pick<PlayerView, 'id' | 'name'> | null | undefined): void {
    if (!player?.id) return;
    this.requested.add(player.id);
    const name = (player.name ?? '').trim();
    if (name) this.names.set(player.id, name);
    else this.names.delete(player.id);
  }

  /** Сбросить кэш по игроку — его переименовали или удалили. */
  invalidate(playerId: string): void {
    this.names.delete(playerId);
    this.requested.delete(playerId);
  }

  /** Сбросить весь кэш (смена пользователя). */
  clear(): void {
    this.names.clear();
    this.requested.clear();
  }

  /**
   * Точечные запросы пачками. Ошибка по одному id не роняет остальные: имя —
   * вспомогательная деталь, экран переживёт его отсутствие.
   */
  private fetchMissing(ids: string[]): Observable<unknown> {
    return from(ids).pipe(
      mergeMap(
        (playerId) =>
          this.api.getPlayerById(playerId).pipe(
            map((player) => (player?.name ?? '').trim()),
            catchError(() => of('')),
            map((name) => {
              if (name) this.names.set(playerId, name);
              return playerId;
            }),
          ),
        PlayerNamesService.CONCURRENCY,
      ),
      scan((count: number) => count + 1, 0),
      last(),
      defaultIfEmpty(0),
    );
  }

  /** Копия кэша по запрошенным id — потребители не получают ссылку на внутреннюю Map. */
  private snapshot(ids: string[]): PlayerNameMap {
    const result: PlayerNameMap = {};
    for (const id of ids) {
      const name = this.names.get(id);
      if (name) result[id] = name;
    }
    return result;
  }
}

/** Усечённый id — то, что показывают вместо неизвестного имени. */
export function shortPlayerId(playerId: string): string {
  return `${playerId.slice(0, 8)}…`;
}
